import { useEffect, useState } from 'preact/hooks';
import {
  platformNotifications,
  NotificationTypeSpec,
  Provider,
  ProviderType,
  CredentialField,
  RoutingRule,
} from './adminApi';

// NotificationsConfigPage — platform-admin UI for the notification
// system. Three tabs: Catalog (read-only inventory), Providers
// (configured email backends), Routing (per-notification-type primary
// + fallback chain).
//
// Distinct from NotificationsPage (the existing notification_log
// VIEWER). This is the MANAGEMENT surface — Settings → Notifications.
//
// v1 scope per docs/implementation-specs/NOTIFICATIONS-V1.md:
// - Read-only catalog with per-type detail + Send Test button
// - Provider CRUD (currently M365 only; auto-detected from registry)
// - Routing rule editor (default catch-all + per-type overrides)
// - No template editor (templates are code-managed)

type Tab = 'catalog' | 'providers' | 'routing';

export function NotificationsConfigPage() {
  const [tab, setTab] = useState<Tab>('catalog');
  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Notifications</h1>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Manage outbound notification types, providers, and routing.
        Per-message log: see "Notifications" in the main nav.
      </p>

      <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--admin-border)', marginBottom: 16 }}>
        <TabBtn active={tab === 'catalog'} onClick={() => setTab('catalog')}>Catalog</TabBtn>
        <TabBtn active={tab === 'providers'} onClick={() => setTab('providers')}>Providers</TabBtn>
        <TabBtn active={tab === 'routing'} onClick={() => setTab('routing')}>Routing</TabBtn>
      </div>

      {tab === 'catalog' && <CatalogTab />}
      {tab === 'providers' && <ProvidersTab />}
      {tab === 'routing' && <RoutingTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px',
        background: active ? 'var(--admin-surface-raised)' : 'transparent',
        color: 'var(--admin-text)',
        border: 'none',
        borderBottom: active ? '2px solid var(--admin-accent)' : '2px solid transparent',
        cursor: 'pointer',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

// ─── Catalog tab ─────────────────────────────────────────────────────

function CatalogTab() {
  const [types, setTypes] = useState<NotificationTypeSpec[]>([]);
  const [selected, setSelected] = useState<NotificationTypeSpec | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    platformNotifications.listCatalog()
      .then((r) => setTypes(r.types || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Loading catalog…</p>;
  if (err) return <p style={{ color: 'var(--admin-danger)' }}>Error: {err}</p>;

  // Group by category for display
  const byCategory: Record<string, NotificationTypeSpec[]> = {};
  for (const t of types) {
    (byCategory[t.category] ||= []).push(t);
  }

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      <div style={{ flex: '0 0 380px', maxHeight: '70vh', overflow: 'auto' }}>
        {Object.entries(byCategory).map(([cat, list]) => (
          <div key={cat} style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, color: 'var(--admin-text-dim)', textTransform: 'uppercase', fontSize: 11, marginBottom: 4 }}>
              {cat}
            </div>
            {list.map((t) => (
              <div
                key={t.typeId}
                onClick={() => setSelected(t)}
                style={{
                  padding: '6px 10px',
                  cursor: 'pointer',
                  background: selected?.typeId === t.typeId ? 'var(--admin-surface-raised)' : 'transparent',
                  color: 'var(--admin-text)',
                  borderRadius: 4,
                  fontSize: 14,
                  // Subtle dim for unwired types so the operator can see
                  // at a glance which entries have a live sender.
                  opacity: t.wired ? 1 : 0.55,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{t.displayName}</span>
                  {!t.wired && (
                    <span
                      title="No active sender for this notification type."
                      style={{
                        fontSize: 10, fontWeight: 600,
                        background: 'var(--admin-border)',
                        color: 'var(--admin-text-dim)',
                        padding: '1px 6px', borderRadius: 8, textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Unwired
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--admin-text-dim)' }}>
                  {t.typeId} · {t.complianceClass}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ flex: 1 }}>
        {selected ? (
          <CatalogDetail spec={selected} />
        ) : (
          <p style={{ color: 'var(--admin-text-dim)' }}>Select a notification type to see details.</p>
        )}
      </div>
    </div>
  );
}

function CatalogDetail({ spec }: { spec: NotificationTypeSpec }) {
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  async function sendTest() {
    if (!recipient) return;
    setSending(true);
    setResult(null);
    try {
      const r = await platformNotifications.sendTestNotification(spec.typeId, recipient);
      setResult({
        ok: r.ok,
        msg: r.ok ? `Sent via ${r.providerUsed}` : (r.errorMessage || 'Unknown error'),
      });
    } catch (e) {
      setResult({ ok: false, msg: e instanceof Error ? e.message : 'Failed' });
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{spec.displayName}</h2>
      <code style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', padding: '2px 6px', borderRadius: 3, fontSize: 13, fontFamily: 'var(--admin-font-mono)' }}>
        {spec.typeId}
      </code>

      <p style={{ marginTop: 16 }}>
        <strong>Trigger:</strong> {spec.triggerDescription}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', marginTop: 16, fontSize: 14 }}>
        <strong>Category:</strong> <span>{spec.category}</span>
        <strong>Compliance:</strong> <span>{spec.complianceClass}</span>
        <strong>Priority:</strong> <span>{spec.priority}</span>
        <strong>Opt-out:</strong> <span>{spec.userOptOut}</span>
        <strong>Channels:</strong> <span>{(spec.supportedChannels || []).join(', ')}</span>
        <strong>Retention:</strong> <span>{spec.retentionClass}</span>
        <strong>Required vars:</strong> <span>{(spec.requiredVars || []).join(', ')}</span>
      </div>

      <h3 style={{ marginTop: 24 }}>Sample payload</h3>
      <pre style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', padding: 12, borderRadius: 4, overflow: 'auto', fontSize: 13, fontFamily: 'var(--admin-font-mono)' }}>
        {JSON.stringify(JSON.parse(spec.samplePayloadJson || '{}'), null, 2)}
      </pre>

      {spec.wired && (
        <>
          <h3 style={{ marginTop: 24 }}>Send test</h3>
          <p style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>
            Sends a wire-format verification email to the address below using the configured routing for this type.
            Sample payload is included as JSON in the body.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              placeholder="recipient@example.com"
              value={recipient}
              onInput={(e) => setRecipient((e.target as HTMLInputElement).value)}
              style={{ flex: 1, padding: 6 }}
            />
            <button onClick={sendTest} disabled={sending || !recipient}>
              {sending ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </>
      )}
      {result && (
        <p style={{ color: result.ok ? 'var(--admin-success)' : 'var(--admin-danger)', marginTop: 8 }}>
          {result.ok ? '✓' : '✗'} {result.msg}
        </p>
      )}
    </div>
  );
}

// ─── Providers tab ───────────────────────────────────────────────────

function ProvidersTab() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    try {
      const [pl, tl] = await Promise.all([
        platformNotifications.listProviders(),
        platformNotifications.listProviderTypes(),
      ]);
      setProviders(pl.providers || []);
      setProviderTypes(tl.types || []);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function testConnection(id: string) {
    try {
      const r = await platformNotifications.testProviderConnection(id);
      alert(r.ok ? `OK: ${r.message}` : `Failed: ${r.message}`);
      reload();
    } catch (e) {
      alert('Test failed: ' + (e instanceof Error ? e.message : ''));
    }
  }

  async function deleteProvider(id: string, name: string) {
    if (!confirm(`Delete provider "${name}"?\nIf any routing rule still references it, this will fail.`)) return;
    try {
      await platformNotifications.deleteProvider(id);
      reload();
    } catch (e) {
      alert('Delete failed: ' + (e instanceof Error ? e.message : ''));
    }
  }

  // Reconnect: re-runs the OAuth flow against an existing row,
  // replacing its tokens. Used when a refresh token is revoked or the
  // user wants to switch the connected mailbox.
  async function reconnect(id: string, name: string) {
    try {
      const url = await platformNotifications.startMicrosoftOAuth({ name, replaceProviderId: id });
      window.location.href = url;
    } catch (e) {
      alert('Reconnect failed: ' + (e instanceof Error ? e.message : ''));
    }
  }

  if (loading) return <p>Loading providers…</p>;

  return (
    <div>
      {err && <p style={{ color: 'var(--admin-danger)' }}>Error: {err}</p>}
      <button onClick={() => setShowAdd(true)} style={{ marginBottom: 16 }}>
        + Add Provider
      </button>

      {providers.length === 0 ? (
        <p style={{ color: 'var(--admin-text-dim)' }}>
          No providers configured yet. Click "Add Provider" to configure your first email backend.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--admin-surface-raised)' }}>
              <th style={th}>Name</th>
              <th style={th}>Type</th>
              <th style={th}>Health</th>
              <th style={th}>Failures</th>
              <th style={th}>Enabled</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => {
              const signedInEmail = parseSignedInEmail(p.configJson);
              const ptype = providerTypes.find(pt => pt.id === p.providerType);
              const editableConfigFields = (ptype?.credentialSchema || []).filter(
                f => !f.secret && f.type !== 'oauth_button'
              );
              return (
                <tr key={p.id} style={{ borderTop: '1px solid var(--admin-border)' }}>
                  <td style={td}>
                    <div>{p.name}</div>
                    {signedInEmail && (
                      <div style={{ fontSize: 11, color: 'var(--admin-text-dim)', marginTop: 2 }}>
                        Connected as {signedInEmail}
                      </div>
                    )}
                    {editableConfigFields.map(f => (
                      <ConfigFieldEditor
                        key={f.key}
                        provider={p}
                        field={f}
                        onSaved={reload}
                      />
                    ))}
                  </td>
                  <td style={td}>{p.providerType}</td>
                  <td style={td}>
                    <HealthBadge status={p.healthStatus} />
                    {p.lastErrorMessage && (
                      <div style={{ fontSize: 11, color: 'var(--admin-text-dim)', marginTop: 2 }}>
                        {p.lastErrorMessage.slice(0, 80)}
                      </div>
                    )}
                  </td>
                  <td style={td}>{p.consecutiveFailures}</td>
                  <td style={td}>{p.enabled ? '✓' : '—'}</td>
                  <td style={td}>
                    <button onClick={() => testConnection(p.id)} style={{ marginRight: 6 }}>Test</button>
                    {p.providerType === 'microsoft365' && (
                      <button onClick={() => reconnect(p.id, p.name)} style={{ marginRight: 6 }}>Reconnect</button>
                    )}
                    <button onClick={() => deleteProvider(p.id, p.name)} style={{ color: 'var(--admin-danger)' }}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddProviderModal
          providerTypes={providerTypes}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); reload(); }}
        />
      )}
    </div>
  );
}

// ConfigFieldEditor renders an inline editor for a single non-secret
// schema field. Pre-fills from the provider's configJson; on Save
// calls UpdateProvider with just the changed field. Used for
// post-connect knobs like M365's from_address.
function ConfigFieldEditor({ provider, field, onSaved }: {
  provider: Provider;
  field: CredentialField;
  onSaved: () => void;
}) {
  let initial = '';
  try {
    const parsed = JSON.parse(provider.configJson || '{}');
    initial = parsed[field.key] || '';
  } catch { /* keep empty */ }

  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const dirty = value !== initial;

  async function save() {
    setSaving(true);
    try {
      await platformNotifications.updateProvider({
        id: provider.id,
        values: { [field.key]: value },
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e instanceof Error ? e.message : ''));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 6, fontSize: 12 }}>
      <label style={{ display: 'block', color: 'var(--admin-text-dim)', marginBottom: 2 }}>
        {field.label}
      </label>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type={field.type === 'email' ? 'email' : 'text'}
          value={value}
          placeholder={field.placeholder || ''}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
          style={{ flex: 1, padding: '4px 6px', fontSize: 12 }}
        />
        <button
          onClick={save}
          disabled={!dirty || saving}
          style={{ fontSize: 12, padding: '4px 10px' }}
        >
          {saving ? '…' : savedFlash ? '✓' : 'Save'}
        </button>
      </div>
      {field.helpText && (
        <div style={{ marginTop: 2, color: 'var(--admin-text-dim)', fontSize: 11 }}>
          {field.helpText}
        </div>
      )}
    </div>
  );
}

function HealthBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    healthy: 'var(--admin-success)',
    degraded: 'var(--admin-warning)',
    failing: 'var(--admin-danger)',
    unknown: 'var(--admin-text-dim)',
  };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, color: 'var(--admin-bg)',
      background: colors[status] || 'var(--admin-text-dim)', fontSize: 11, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

function AddProviderModal({ providerTypes, onClose, onCreated }: {
  providerTypes: ProviderType[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [selectedType, setSelectedType] = useState<string>(providerTypes[0]?.id || '');
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const t = providerTypes.find(x => x.id === selectedType);

  async function submit() {
    if (!name || !selectedType) return;
    setSubmitting(true);
    setErr(null);
    try {
      await platformNotifications.createProvider({
        providerType: selectedType,
        name,
        values,
        enabled: true,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={modalBackdrop}>
      <div style={modalBox}>
        <h2 style={{ marginTop: 0 }}>Add Email Provider</h2>

        <label>Provider type:</label>
        <select value={selectedType} onChange={(e) => { setSelectedType((e.target as HTMLSelectElement).value); setValues({}); }} style={{ width: '100%', padding: 6, marginBottom: 12 }}>
          {providerTypes.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
        </select>

        <label>Display name:</label>
        <input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} placeholder="e.g. M365 Primary" style={{ width: '100%', padding: 6, marginBottom: 12 }} />

        {t?.helpUrl && (
          <p style={{ fontSize: 13, marginBottom: 12 }}>
            <a href={t.helpUrl} target="_blank" rel="noreferrer">Setup docs ↗</a>
          </p>
        )}

        {t?.credentialSchema.map(field => {
          // For OAuth providers, only the oauth_button shows on the
          // Add modal — other fields (e.g. from_address) are
          // configurable post-connect from the provider row, since
          // the OAuth round-trip can't carry pre-typed form values
          // back through Microsoft's redirect.
          if (hasOauthButton(t) && field.type !== 'oauth_button') {
            return null;
          }
          // OAuth-button schema: render a Connect link instead of inputs.
          // Clicking redirects the browser to /auth/{provider}/start which
          // takes care of the rest (consent screen, callback, row creation).
          if (field.type === 'oauth_button') {
            return (
              <div key={field.key} style={{ marginBottom: 12, padding: 12, background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 4 }}>
                <p style={{ margin: '0 0 8px', fontSize: 13 }}>{field.label}</p>
                {field.helpText && <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--admin-text-dim)' }}>{field.helpText}</p>}
                <button
                  type="button"
                  disabled={!name.trim() || submitting}
                  onClick={async () => {
                    setSubmitting(true);
                    setErr(null);
                    try {
                      const url = await startOAuthFor(t.id, name);
                      window.location.href = url;
                    } catch (e) {
                      setErr(e instanceof Error ? e.message : 'Could not start OAuth flow');
                      setSubmitting(false);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--admin-accent)',
                    color: 'var(--admin-bg)',
                    border: 'none',
                    borderRadius: 4,
                    fontWeight: 600,
                    cursor: name.trim() ? 'pointer' : 'not-allowed',
                    opacity: name.trim() ? 1 : 0.5,
                  }}
                >
                  {submitting ? 'Redirecting…' : 'Connect Microsoft 365 →'}
                </button>
              </div>
            );
          }
          return (
            <div key={field.key} style={{ marginBottom: 12 }}>
              <label>{field.label}{field.required && ' *'}</label>
              <input
                type={field.type === 'password' ? 'password' : 'text'}
                value={values[field.key] || ''}
                placeholder={field.placeholder || ''}
                onInput={(e) => setValues({ ...values, [field.key]: (e.target as HTMLInputElement).value })}
                style={{ width: '100%', padding: 6 }}
              />
              {field.helpText && <small style={{ color: 'var(--admin-text-dim)' }}>{field.helpText}</small>}
            </div>
          );
        })}

        {err && <p style={{ color: 'var(--admin-danger)' }}>{err}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} disabled={submitting}>{hasOauthButton(t) ? 'Close' : 'Cancel'}</button>
          {!hasOauthButton(t) && (
            <button onClick={submit} disabled={submitting || !name}>{submitting ? 'Creating…' : 'Create'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Routing tab ─────────────────────────────────────────────────────

function RoutingTab() {
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [types, setTypes] = useState<NotificationTypeSpec[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    try {
      const [rl, pl, cl] = await Promise.all([
        platformNotifications.listRoutingRules(),
        platformNotifications.listProviders(),
        platformNotifications.listCatalog(),
      ]);
      setRules(rl.rules || []);
      setProviders(pl.providers || []);
      setTypes(cl.types || []);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  async function upsertDefault(primaryProviderId: string) {
    if (!primaryProviderId) return;
    try {
      await platformNotifications.upsertRoutingRule({
        notificationType: '',
        primaryProviderId,
        fallbackProviderIds: [],
        enabled: true,
      });
      reload();
    } catch (e) {
      alert('Update failed: ' + (e instanceof Error ? e.message : ''));
    }
  }

  if (loading) return <p>Loading routing…</p>;
  if (err) return <p style={{ color: 'var(--admin-danger)' }}>{err}</p>;

  const defaultRule = rules.find(r => r.notificationType === '');
  const typedRules = rules.filter(r => r.notificationType !== '');
  const providerName = (id: string) => providers.find(p => p.id === id)?.name || `<unknown ${id.slice(0, 8)}>`;

  return (
    <div>
      <h3>Default rule (catch-all)</h3>
      <p style={{ color: 'var(--admin-text-dim)', fontSize: 13 }}>
        Used for any notification type without an explicit rule below. Required before any send works.
      </p>
      <div style={{ marginBottom: 24, padding: 12, background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 4 }}>
        <label>Primary provider:</label>
        <select
          value={defaultRule?.primaryProviderId || ''}
          onChange={(e) => upsertDefault((e.target as HTMLSelectElement).value)}
          style={{ width: '100%', padding: 6 }}
        >
          <option value="">— none configured —</option>
          {providers.map(p => <option key={p.id} value={p.id}>{p.name} ({p.providerType})</option>)}
        </select>
        {!defaultRule && providers.length > 0 && (
          <p style={{ fontSize: 12, color: 'var(--admin-warning)', marginTop: 6 }}>
            ⚠ No default rule set — every send will fail until one is configured.
          </p>
        )}
      </div>

      <h3>Per-type overrides</h3>
      {typedRules.length === 0 ? (
        <p style={{ color: 'var(--admin-text-dim)', fontSize: 13 }}>
          No per-type overrides. Add one when a specific notification type needs different routing
          (e.g., transactional via best-deliverability provider, marketing via cheapest).
          v1 UI ships read-only listing; CLI / future UI iterates here.
        </p>
      ) : (
        <table style={{ width: '100%', fontSize: 14 }}>
          <thead><tr><th style={th}>Type</th><th style={th}>Primary</th><th style={th}>Fallbacks</th></tr></thead>
          <tbody>
            {typedRules.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--admin-border)' }}>
                <td style={td}>{r.notificationType}</td>
                <td style={td}>{providerName(r.primaryProviderId)}</td>
                <td style={td}>{(r.fallbackProviderIds || []).map(providerName).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p style={{ marginTop: 24, fontSize: 12, color: 'var(--admin-text-dim)' }}>
        Catalog → click any type for per-type detail + Send test button.
        Per-type routing override editor (full CRUD) ships in a follow-up UI iteration; v1 surface is the default rule.
      </p>
      <p style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>
        Total: {rules.length} rule(s), {providers.length} provider(s), {types.length} notification type(s) registered ({types.filter(t => t.wired).length} wired with a live sender).
      </p>
    </div>
  );
}

// ─── Inline styles (keeps this page self-contained) ──────────────────

const th: any = { textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13 };
const td: any = { padding: '8px 12px', verticalAlign: 'top' };

const modalBackdrop: any = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalBox: any = {
  background: 'var(--admin-surface)',
  color: 'var(--admin-text)',
  border: '1px solid var(--admin-border)',
  padding: 24,
  borderRadius: 8,
  width: '90%',
  maxWidth: 520,
  maxHeight: '90vh',
  overflow: 'auto',
};

// ─── Helpers ─────────────────────────────────────────────────────────

// hasOauthButton returns true if the provider type uses an OAuth flow
// instead of credential-input fields. Drives whether the modal shows
// a "Create" button or just a "Close" button (creation happens via
// the OAuth callback, not via direct CreateProvider RPC).
function hasOauthButton(t: ProviderType | undefined): boolean {
  return !!t?.credentialSchema.some(f => f.type === 'oauth_button');
}

// startOAuthFor calls the appropriate per-provider OAuth start
// endpoint and returns the redirect URL. Today only microsoft365 is
// supported; future OAuth providers (Google Workspace, etc.) add a
// case here + their own start endpoint server-side.
async function startOAuthFor(providerTypeId: string, name: string): Promise<string> {
  switch (providerTypeId) {
    case 'microsoft365':
      return platformNotifications.startMicrosoftOAuth({ name });
    default:
      throw new Error(`No OAuth flow registered for provider type "${providerTypeId}"`);
  }
}

// parseSignedInEmail extracts the connected mailbox from a Provider's
// configJson string (set by the OAuth callback for OAuth providers).
// Returns empty string for non-OAuth providers or unparseable JSON.
function parseSignedInEmail(configJson: string): string {
  if (!configJson) return '';
  try {
    const parsed = JSON.parse(configJson);
    return parsed.signed_in_email || '';
  } catch {
    return '';
  }
}
